import React, { useRef, useState } from "react";

import { SEO } from "../../seo/SEO";
import styled from "styled-components";
import { useTypedResource } from "../../i18n/languagehandler";

const HeroSection = styled.section`
  max-width: 1200px;
  margin: 20px auto 0;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  min-height: 60vh;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: start;
    min-height: auto;
  }
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 60px auto 0;
  padding: 0 20px;
`;

const Title = styled.h2`
  color: var(--text);
  font-size: 36px;
  margin: 0 0 20px 0;
  line-height: 1.2;
  text-transform: uppercase;
`;

const BodyText = styled.p`
  color: var(--text-secondary);
  font-size: 18px;
  line-height: 1.6;
  margin: 0 0 20px 0;
`;

const ExpertiseText = styled.p`
  color: var(--text);
  font-size: 18px;
  font-weight: 500;
  line-height: 1.6;
  margin: 0 0 20px 0;
`;

const WorkText = styled.p`
  color: var(--text-secondary);
  font-size: 18px;
  line-height: 1.6;
  margin: 0 0 20px 0;
`;

const ToggleButton = styled.button`
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 20px 0;
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(55, 90, 127, 0.3);
  }
`;

const LongContent = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? "block" : "none")};
  animation: ${(props) => (props.isVisible ? "fadeIn 0.3s ease-in" : "none")};
  background: #fff;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  margin-top: 20px;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LinkText = styled.p`
  color: var(--primary);
  font-size: 18px;
  line-height: 1.6;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExternalLink = styled.a`
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  &:hover {
    color: var(--primary-dark);
    text-decoration: underline;
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  max-width: 600px;
  border-radius: 16px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  &:hover {
    transform: translateY(-5px);
  }
`;

// Removed secondary stacked image to keep a single strong visual

export const AboutAida: React.FC = () => {
  const [showLongVersion, setShowLongVersion] = useState(false);
  const longContentRef = useRef<HTMLDivElement>(null);
  const res = useTypedResource<{
    title: string;
    shortDescription: string;
    shortExpertise: string;
    shortWork: string;
    longDescription: string;
    longResearch: string;
    currentStudy: string;
    parentsRole: string;
    schoolCollaboration: string;
    swissExample: string;
    danishExample: string;
    expertRole: string;
    readMore: string;
    showMore: string;
    showLess: string;
    swissLink: string;
    danishLink: string;
    researchLink: string;
  }>("about");

  return (
    <main>
      <SEO
        title={res.title}
        description={res.shortDescription}
        path="/about"
        image={"/assets/About Aida/Billede profil.jpeg"}
      />

      <HeroSection>
        <div>
          <Title>{res.title}</Title>
          <BodyText>{res.shortDescription}</BodyText>
          <ExpertiseText>{res.shortExpertise}</ExpertiseText>
          <WorkText>{res.shortWork}</WorkText>

          <ToggleButton
            onClick={() => {
              const newShowLongVersion = !showLongVersion;
              setShowLongVersion(newShowLongVersion);

              // Auto-scroll to the long content when showing it
              if (newShowLongVersion && longContentRef.current) {
                setTimeout(() => {
                  longContentRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }, 100); // Small delay to ensure content is rendered
              }
            }}
          >
            {showLongVersion ? res.showLess : res.showMore}
          </ToggleButton>
        </div>
        <div>
          <ProfileImage
            src={
              process.env.PUBLIC_URL + "/assets/About Aida/Billede profil.jpeg"
            }
            alt="Aida Bikic"
          />
        </div>
      </HeroSection>

      <ContentSection>
        <LongContent ref={longContentRef} isVisible={showLongVersion}>
          <BodyText>{res.longDescription}</BodyText>
          <BodyText>{res.longResearch}</BodyText>
          <BodyText>{res.currentStudy}</BodyText>
          <BodyText>{res.parentsRole}</BodyText>
          <BodyText>
            {res.schoolCollaboration}
            <br />
            <ExternalLink
              href={res.swissLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {res.swissExample}
            </ExternalLink>
            <br />
            <ExternalLink
              href={res.danishLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {res.danishExample}
            </ExternalLink>
          </BodyText>
          <BodyText>{res.expertRole}</BodyText>
          <LinkText>
            <ExternalLink
              href={res.researchLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {res.readMore}
            </ExternalLink>
          </LinkText>
        </LongContent>
      </ContentSection>

      {/* Removed secondary image section as requested */}
    </main>
  );
};
